import { getAnonymousRefinementCountKey } from './anonymous-refinement-storage'

/**
 * Central keys for the living project guide (anonymous localStorage, session,
 * and post-signup claim). Task 1.1 inventory for Story 2.4.
 *
 * - `diy_answers_{slug}` — Q&A answers (local + session copy for URL-preset flow)
 * - `diy_anon_refinement_count_{slug}` — anonymous refinement cap (2.3)
 * - `diy_personalize_dismissed_{slug}` — personalize prompt dismissed
 * - `diy_sections_{slug}` — step expand/collapse in session (ProjectGuideSteps)
 * - `diy_signup_intent_from_guide_{slug}` (session) — set when navigating to
 *   sign-in/up from the in-guide CTA; consumed on return for auto-claim
 * - `diy_living_claim_done_{slug}` — claim finished in this browser (idempotency)
 */
export const DIY_ANSWERS_KEY_PREFIX = 'diy_answers_'
export const DIY_PERSONALIZE_DISMISSED_PREFIX = 'diy_personalize_dismissed_'
export const DIY_SECTIONS_SESSION_PREFIX = 'diy_sections_'

const SIGNUP_INTENT_FROM_GUIDE_PREFIX = 'diy_signup_intent_from_guide_'
const LIVING_CLAIM_DONE_PREFIX = 'diy_living_claim_done_'

export function getDiyAnswersKey(templateSlug: string): string {
  return `${DIY_ANSWERS_KEY_PREFIX}${templateSlug}`
}

export function getDiyPersonalizeDismissedKey(templateSlug: string): string {
  return `${DIY_PERSONALIZE_DISMISSED_PREFIX}${templateSlug}`
}

/**
 * Parse stored Q&A (same format as `upsertProjectFromDiyGuide` `refinementAnswers`).
 * Invalid or empty storage yields `{}`.
 */
export function readDiyRefinementAnswersFromLocalStorage(
  templateSlug: string
): Record<string, string> {
  if (typeof localStorage === 'undefined') return {}
  try {
    const raw = localStorage.getItem(getDiyAnswersKey(templateSlug))
    if (!raw) return {}
    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
    const out: Record<string, string> = {}
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof v === 'string') out[k] = v
    }
    return out
  } catch {
    return {}
  }
}

function signupIntentKey(templateSlug: string): string {
  return `${SIGNUP_INTENT_FROM_GUIDE_PREFIX}${templateSlug}`
}

/**
 * In-guide CTA / FAB “Save” should call this so post-oauth we auto-claim (AC #1).
 */
export function setSignupIntentFromGuide(templateSlug: string): void {
  if (typeof sessionStorage === 'undefined') return
  try {
    sessionStorage.setItem(signupIntentKey(templateSlug), '1')
  } catch {
    // ignore
  }
}

/**
 * True when the in-guide CTA set session intent; does not clear the key.
 * Use for deciding auto-claim; call {@link consumeSignupIntentFromGuide} only after
 * a successful `upsert` so failed mutations can retry.
 */
export function hasSignupIntentFromGuide(templateSlug: string): boolean {
  if (typeof sessionStorage === 'undefined') return false
  try {
    return sessionStorage.getItem(signupIntentKey(templateSlug)) === '1'
  } catch {
    return false
  }
}

/**
 * If intent was set, clear it and return true (one shot).
 * Prefer after successful claim; see {@link hasSignupIntentFromGuide}.
 */
export function consumeSignupIntentFromGuide(templateSlug: string): boolean {
  if (typeof sessionStorage === 'undefined') return false
  try {
    const k = signupIntentKey(templateSlug)
    if (sessionStorage.getItem(k) === '1') {
      sessionStorage.removeItem(k)
      return true
    }
  } catch {
    // ignore
  }
  return false
}

export function isLivingGuideClaimComplete(templateSlug: string): boolean {
  if (typeof localStorage === 'undefined') return false
  try {
    return localStorage.getItem(`${LIVING_CLAIM_DONE_PREFIX}${templateSlug}`) === '1'
  } catch {
    return false
  }
}

export function markLivingGuideClaimComplete(templateSlug: string): void {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(`${LIVING_CLAIM_DONE_PREFIX}${templateSlug}`, '1')
  } catch {
    // ignore
  }
}

/**
 * After successful claim: drop anonymous-only keys; answers may stay for UX parity
 * (they match `refinementAnswers` on the project).
 */
export function clearPostClaimAnonymousOnlyKeys(templateSlug: string): void {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.removeItem(getAnonymousRefinementCountKey(templateSlug))
  } catch {
    // ignore
  }
}

/**
 * Dismiss “claim” (AC #2): clear stored anonymous work for this template.
 */
export function clearAllLivingGuideUserStorageForSlug(templateSlug: string): void {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.removeItem(getDiyAnswersKey(templateSlug))
    localStorage.removeItem(getAnonymousRefinementCountKey(templateSlug))
    localStorage.removeItem(getDiyPersonalizeDismissedKey(templateSlug))
  } catch {
    // ignore
  }
  if (typeof sessionStorage === 'undefined') return
  try {
    sessionStorage.removeItem(getDiyAnswersKey(templateSlug))
    sessionStorage.removeItem(`${DIY_SECTIONS_SESSION_PREFIX}${templateSlug}`)
  } catch {
    // ignore
  }
}
